import { Document } from "../document/Document";
import { ISize, IThickness, ScreenDirection, PaintStyle } from "../drawing";
import { IValueRange } from "../system";

export interface IWizard extends IWizardRegistationValidatable, IWizardAttributesContainable<IWholeWizardAttributes> {
  pages: IWizardPage[];
  registration: IWizardRegistration;
  registeredPages: IWizardPage[];
  scripts: IWizardScript[];
}

export interface IWizardAttributesContainable<TAttributes extends IWizardAttributes = IWizardAttributes> {
  attributes: TAttributes;
}

export interface IWizardAttributes {
  fontSize: string;
}

export interface IWholeWizardAttributes extends IWizardAttributes {
  scales: number[];
}

export interface IWizardRegistationValidatable {
  isRegisterable(source: Document): boolean;
}

export interface IWizardValidatable {
  isEnable(source: Document): boolean;
}

export interface IWizardVisibilityValidatable {
  isVisible(source: Document): boolean;
}

export interface IWizardPage extends IWizardContentsContainable, IWizardValidatable, IWizardRegistationValidatable, IWizardAttributesContainable {}

export interface IWizardContentsContainable {
  contents: IWizardContent[];
  gap?: string;
  contentMargin?: string;
}

export function instanceOfIWizardContentsContainable(instance: any): instance is IWizardContentsContainable {
  return instance
      && (instance.contents !== undefined);
}

export interface IWizardScript {
  allowed: string;
}

export interface IWizardRegistration {
  isTemporary: boolean;
  shouldAuthenticate: boolean;
}

export interface IWizardContent extends IWizardValidatable, IWizardRegistationValidatable, IWizardVisibilityValidatable, IWizardAttributesContainable {
  privateId: string;
  type: WizardContentTypes;
  margin: IThickness<string>;
  isMandatory(source: Document): boolean;
}

export type WizardContentTypes
= "container"
| "heading-indicator"
| "fixed-text-indicator"
| "text-editor"
| "text-input"
| "date-input"
| "number-input"
| "bar-input"
| "radio-selector"
| "check-selector"
| "patient-indicator"
| "format-indicator"
| "media-indicator"
| "horizontal-container"
| "stack-container"
| "image-file-selector"
| "image-capture-input"
| "image-sign-input"
| "claio-image-selector"
| "claio-schema-selector"
| "media-remover"
| "media-rotater"
| "text-indicator"
| "user-selector"
| "login-user-selector"
| "radio-selectors"
| "not-implemented";

export interface IWizardFixedTextIndicatable {
  fixedText: string;
}

export function instanceOfIFixedTextIndicatable(instance: any): instance is IWizardFixedTextIndicatable {
  return instance
      && (instance.fixedText !== undefined);
}

export interface IWizardHeadingIndicatable extends IWizardFixedTextIndicatable {
}

export function instanceOfIWizardHeadingIndicatable(instance: any): instance is IWizardHeadingIndicatable {
  return instance
      && instanceOfIFixedTextIndicatable(instance);
}

export interface IWizardFormatReferable {
  formatIdentifier: IWizardFormatIdentifiable;
}

export function instanceOfIWizardFormatReferable(instance: any): instance is IWizardFormatReferable {
  return instance
      && (instance.formatIdentifier !== undefined);
}

export interface IWizardFormatIdentifiable {
  formatId: number;
}

export interface IWizardObjectReferable {
  objectIdentifier: IWizardObjectIdentifiable;
}

export function instanceOfIWizardObjectReferable(instance: any): instance is IWizardObjectReferable {
  return instance
      && (instance.objectIdentifier !== undefined);
}

export interface IWizardObjectIdentifiable {
  id: string; // obj-id.
}

export interface IWizardSizeSpecifiable {
  size: ISize<string>;
}

export function instanceOfIWizardSizeSpecifiable(instance: any): instance is IWizardSizeSpecifiable {
  return instance
      && (instance.size !== undefined);
}

export interface IWizardObjectDecoratable {
  decoration: IWizardInputDecoratable;
}

export function instanceOfIWizardObjectDecoratable(instance: any): instance is IWizardObjectDecoratable {
  return instance
      && (instance.decoration !== undefined);
}

export interface IWizardInputDecoratable {
  prefix?: string;
  postfix?: string;
}

export interface IWizardObjectSelectable {
  objectOptions: IWizardObjectOption[];
}

export function instanceOfIWizardObjectSelectable(instance: any): instance is IWizardObjectSelectable {
  return instance
      && (instance.objectOptions !== undefined);
}

export interface IWizardObjectOption extends IWizardObjectReferable {
  text?: string;
  content?: IWizardContent;
  isHorizontal?: boolean;
}

export interface IWizardObjectRotatable {
  angle?: number;
}

export function instanceOfIWizardObjectRotatable(instance: any): instance is IWizardObjectRotatable {
  return instance
      && (instance.angle !== undefined);
}

export interface IWizardBarDrawable {
  range: IValueRange;
  stepCount: number;
  direction: ScreenDirection;
  paintStyle: PaintStyle;
}

export function instanceOfIWizardBarDrawable(instance: any): instance is IWizardBarDrawable {
  return instance
      && (instance.range !== undefined)
      && (instance.stepCount !== undefined)
      && (instance.direction !== undefined)
      && (instance.paintStyle !== undefined);
}

export interface IWizardObjectMultiSelectable {
  isMultiSelectable: boolean;
}

export function instanceOfIWizardObjectMultiSelectable(instance: any): instance is IWizardObjectMultiSelectable {
  return instance
    && (instance.isMultiSelectable !== undefined);
}

export interface IWizardObjectPositionSpecifiable {
  isHorizontal: boolean;
}

export function instanceOfIWizardObjectPositionSpecifiable(instance: any): instance is IWizardObjectPositionSpecifiable {
  return instance
    && (instance.isHorizontal !== undefined);
}

export interface IWizardObjectOptionsVisibilitySpecifiable {
  visibleOptionIndexes?: number[];
}

export function instanceOfIWizardObjectOptionsVisibilitySpecifiable(instance: any): instance is IWizardObjectOptionsVisibilitySpecifiable {
  return instance
      && (instance.visibleOptionIndexes !== undefined);
}

export interface IWizardObjectAlternativeOptionsSelectable {
  alternativeOptions: IWizardObjectAlternativeOption[];
}

export function instanceOfIWizardObjectAlternativeOptionsSelectable(instance: any): instance is IWizardObjectAlternativeOptionsSelectable {
  return instance
    && (instance.alternativeOptions !== undefined);
}

export interface IWizardObjectAlternativeOption {
  index: number;
  text: string;
}

export function instanceOfIWizardObjectAlternativeOption(instance: any): instance is IWizardObjectAlternativeOption {
  return instance
    && (instance.index !== undefined)
    && (instance.text !== undefined);
}

export interface IWizardObjectProcessorContainable {
  processor?: IWizardContent;
}

export function instanceOfIWizardObjectProcessorContainable(instance: unknown): instance is IWizardObjectProcessorContainable {
  const casted = instance as IWizardObjectProcessorContainable;
  return !!casted
      && !!casted.processor; // 未指定もありえるが未指定の場合は選択できないのだから未実装として扱う.
}
